import {
  createV2Production,
  createV2ProductionResult,
  createV2Repetition,
  createV2RepetitionResult,
} from './factories';
import { assertV2MonitorController } from './controller';
import {
  V2_COMPLETION_REASON,
  V2_RESULT_STATUS,
  V2_RUNTIME_STATUS,
} from './types';

const createProductionId = ({ activityId, monitorType, productionIndex, startedAt }) => (
  [activityId || 'activity', monitorType, productionIndex, startedAt].join('-')
);

export const createV2RuntimeCoordinator = ({ controller, monitor }) => {
  const monitorController = assertV2MonitorController(controller);
  let effectiveMonitor = monitor;
  let status = V2_RUNTIME_STATUS.IDLE;
  let activeProduction = null;
  let activeRepetition = null;
  let context = null;
  let completedProductionIndex = null;

  const startRepetition = (nextContext) => {
    const startedAt = Date.now();
    activeRepetition = createV2Repetition({
      repetitionIndex: nextContext.repetitionIndex,
      startedAt,
    });
    monitorController.startRepetition(nextContext);
    status = V2_RUNTIME_STATUS.REPETITION_ACTIVE;
  };

  const startProduction = (nextContext) => {
    const startedAt = Date.now();
    activeProduction = createV2Production({
      productionId: createProductionId({
        activityId: nextContext.activityId,
        monitorType: effectiveMonitor.type,
        productionIndex: nextContext.productionIndex,
        startedAt,
      }),
      monitor: effectiveMonitor,
      stimulus: nextContext.stimulus,
      productionIndex: nextContext.productionIndex,
      startedAt,
    });
    monitorController.startProduction(nextContext);
    startRepetition(nextContext);
  };

  const sync = (nextContext) => {
    context = nextContext;
    if (!nextContext.active) return;

    if (!activeProduction
      && nextContext.productionIndex !== completedProductionIndex
    ) {
      startProduction(nextContext);
      return;
    }

    if (activeProduction
      && !activeRepetition
      && activeProduction.repetitions.length <= nextContext.repetitionIndex
    ) {
      startRepetition(nextContext);
    }
  };

  const finishRepetition = async (reason = V2_COMPLETION_REASON.USER_NEXT) => {
    if (!activeProduction || !activeRepetition || !context) {
      return { status: V2_RESULT_STATUS.INCOMPLETE };
    }

    status = V2_RUNTIME_STATUS.FINALIZING_REPETITION;
    const outcome = await monitorController.finishRepetition(reason);
    if (outcome.status === V2_RESULT_STATUS.INCOMPLETE) {
      status = V2_RUNTIME_STATUS.REPETITION_ACTIVE;
      return outcome;
    }

    const repetition = createV2RepetitionResult({
      repetition: activeRepetition,
      endedAt: Date.now(),
      reason,
      result: outcome.result,
      status: outcome.status,
    });
    const repetitions = [...activeProduction.repetitions, repetition];
    activeProduction = { ...activeProduction, repetitions };
    activeRepetition = null;

    if (repetitions.length < context.repetitionCount) {
      status = V2_RUNTIME_STATUS.IDLE;
      return {
        status: outcome.status,
        repetition,
        productionCompleted: false,
      };
    }

    status = V2_RUNTIME_STATUS.FINALIZING_PRODUCTION;
    const productionResult = await monitorController.finishProduction({
      context,
      repetitions,
    });
    const production = createV2ProductionResult({
      production: activeProduction,
      repetitions,
      endedAt: Date.now(),
      reason: V2_COMPLETION_REASON.REPETITIONS_COMPLETE,
      result: productionResult,
    });
    completedProductionIndex = context.productionIndex;
    activeProduction = null;
    status = V2_RUNTIME_STATUS.IDLE;

    return {
      status: outcome.status,
      repetition,
      production,
      productionCompleted: true,
    };
  };

  const pause = () => {
    if (status !== V2_RUNTIME_STATUS.REPETITION_ACTIVE) return;
    monitorController.pause();
    status = V2_RUNTIME_STATUS.PAUSED;
  };

  const resume = () => {
    if (status !== V2_RUNTIME_STATUS.PAUSED) return;
    monitorController.resume();
    status = V2_RUNTIME_STATUS.REPETITION_ACTIVE;
  };

  const updateConfig = (config) => {
    effectiveMonitor = { ...effectiveMonitor, config };
    if (activeProduction) {
      activeProduction = { ...activeProduction, monitor: effectiveMonitor };
    }
    monitorController.updateConfig(config);
  };

  const cancel = () => {
    monitorController.cancel();
    activeProduction = null;
    activeRepetition = null;
    context = null;
    completedProductionIndex = null;
    status = V2_RUNTIME_STATUS.IDLE;
  };

  return {
    sync,
    finishRepetition,
    pause,
    resume,
    updateConfig,
    cancel,
    restore: monitorController.restore,
    getStatus: () => status,
    getActiveProduction: () => activeProduction,
  };
};